import { createPinia, defineStore, type Pinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'

// Type definitions
interface IAuthData {
  authHeader: string
}

interface IFormData {
  birthDate: string
  userName: string
}

interface IAppState extends IFormData, IAuthData {}

// Type guards
const isValidDate = (date: string): boolean => {
  if (!date) return false
  const dateRegex = /^\d{6}$/
  return dateRegex.test(date)
}

const isValidUserName = (name: string): boolean => {
  return name.length > 0 && name.length <= 100
}

// Store definition
export const useAppStore = defineStore('disc-survey-app-store', {
  state: (): IAppState => ({
    birthDate: '',
    userName: '',
    authHeader: '',
  }),

  getters: {
    isFormComplete(): boolean {
      return Boolean(this.birthDate && this.userName)
    },

    isFormValid(): boolean {
      return isValidDate(this.birthDate) && isValidUserName(this.userName)
    },

    formData(): IFormData {
      return {
        birthDate: this.birthDate,
        userName: this.userName,
      }
    },

    authData(): IAuthData {
      return {
        authHeader: this.authHeader,
      }
    },

    hasAuth(): boolean {
      return Boolean(this.authHeader)
    },
  },

  actions: {
    setFormData(data: Partial<IFormData>): void {
      if (data.birthDate !== undefined) {
        this.birthDate = data.birthDate
      }
      if (data.userName !== undefined) {
        this.userName = data.userName
      }
    },

    setBirthDate(birthDate: string): void {
      if (!isValidDate(birthDate) && birthDate !== '') {
        console.warn('Invalid date format. Expected: YYMMDD')
      }
      this.birthDate = birthDate
    },

    setUserName(userName: string): void {
      if (!isValidUserName(userName) && userName !== '') {
        console.warn('Invalid user name. Must be between 1-100 characters')
      }
      this.userName = userName
    },

    setAuthHeader(authHeader: string): void {
      this.authHeader = authHeader
    },

    initialize(): void {
      this.birthDate = ''
      this.userName = ''
      this.authHeader = ''
    },

    clearFormData(): void {
      this.birthDate = ''
      this.userName = ''
    },

    clearAuthData(): void {
      this.authHeader = ''
    },

    clearAll(): void {
      this.birthDate = ''
      this.userName = ''
      this.authHeader = ''
    },
  },

  persist: {
    key: 'disc-survey-app',
    storage: localStorage,
    pick: ['birthDate', 'userName', 'authHeader'],
  },
})

// Pinia 인스턴스 생성 및 설정
const pinia = createPinia()
pinia.use(piniaPluginPersistedstate)

export default pinia
